import currencyImg from 'assets/svg/currency.svg';
import locationImg from 'assets/svg/location.svg';
import timeImg from 'assets/svg/time.svg';

import color from 'atoms/colors/colors';

// Quarks
import Flex from 'quarks/Flex';
import Image from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import ComponentButton from 'components/Button/ComponentButton';

// Types
import type { ContentfulComponentCard } from 'graphqlTypes';
import type { FC } from 'react';

interface JobBoardCardProps
  extends FlexProps,
    Omit<ContentfulComponentCard, 'id' | 'children' | 'contentful_id' | 'internal' | 'node_locale'> {
  jobFamily?: string;
  title?: string;
  Cost_Center_Hierarchy?: string;
  timeType?: string;
  locations?: any[];
  jobRequisitionId?: string;
  url?: string;
}

const textWrapCSS = `
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const JobBoardCard: FC<JobBoardCardProps> = ({
  jobFamily,
  title,
  Cost_Center_Hierarchy,
  timeType,
  locations,
  jobRequisitionId,
  url,
}) => (
  <Link href={url || ''} textDecoration="none" width="100%">
    <Flex
      position="relative"
      width="100%"
      height="100%"
      justifyContent="space-between"
      flexDirection="column"
      borderRadius="16px"
      overflowX="hidden"
      overflowY="hidden"
      cursor="pointer"
      paddingAll={24}
      border="1px solid"
      borderColor="gray-200"
      gap="12px"
      transition="300ms ease-in-out"
      hover={{ boxShadow: 'lg' }}
      data-cy="jobCard"
      css={`
        &:hover {
          .MuiButton-root {
            background: ${color.green[300]} !important;
          }
        }
      `}
    >
      <Flex flexDirection="column" gap="12px">
        {jobFamily && (
          <Text textStyle="textXs" textColor="gray-900" data-cy="jobFamily" fontWeight="medium">
            {jobFamily}
          </Text>
        )}
        {title && (
          <Text textStyle="textLg" textColor="gray-900" fontWeight="bold" data-cy="title" css={textWrapCSS}>
            {title.replace(/ \((.*)\)/, '')}
          </Text>
        )}
        {Cost_Center_Hierarchy && (
          <Text textStyle="textSm" textColor="gray-700" data-cy="costCenterHierarchy" css={textWrapCSS}>
            {Cost_Center_Hierarchy}
          </Text>
        )}
        <Flex width="100%" marginTop={12} flexWrap="wrap" gap="16px 24px">
          {locations?.map((loc: any) => (
            <Flex gap="12px" alignItems="center" key={loc.locationName}>
              <Image
                alt="Location SVG Image"
                image={{ file: { url: locationImg } }}
                width="16px"
                height="16px"
                objectFit="contain"
                objectPosition="center"
              />
              <Text textStyle="textXs" data-cy="location" textColor="gray-700">
                <div>{loc.locationName}</div>
              </Text>
            </Flex>
          ))}
          {timeType && (
            <Flex gap="12px" alignItems="center">
              <Image
                alt="Time SVG Image"
                image={{ file: { url: timeImg } }}
                width="16px"
                height="16px"
                objectFit="contain"
                objectPosition="center"
              />
              <Text textStyle="textXs" data-cy="timeType" textColor="gray-700">
                {timeType}
              </Text>
            </Flex>
          )}
          {jobRequisitionId && (
            <Flex gap="12px" alignItems="center">
              <Image
                alt="Currency SVG Image"
                image={{ file: { url: currencyImg } }}
                width="16px"
                height="16px"
                objectFit="contain"
                objectPosition="center"
              />
              <Text textStyle="textXs" data-cy="jobRequisitionId" textColor="gray-700">
                {jobRequisitionId}
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Flex width="100%" marginTop={12}>
        <ComponentButton
          link={url}
          label="Apply Now"
          dataCy="applyButton"
          css={`
            max-width: 100% !important;
            background: ${color.green[50]} !important;
            border: 1px solid ${color.green[300]} !important;
          `}
        />
      </Flex>
    </Flex>
  </Link>
);

export default JobBoardCard;
